import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateOrderAddProductForm } from "../../../components/order/CreateOrderAddProductForm";
import {
  AddProductToOrderFromFormDocument,
  GetOrderByUniqueAttributeDocument,
  ItemType,
  ProductTypeV2,
} from "/@/generated/graphql";
import { useCombinedWorkflowOrderStore } from "/@/shared/stores/orders/create-combined-workflow-store.store";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import { CreateOrderStep } from "/@/shared/types/orders/createOrder.types";

import { useV2ProductTypes } from "@jasper/product-workflows";
import { useOrderConfiguration } from "@jasper/use-cases";
import { notifications } from "@mantine/notifications";

type CreateOrderAddProductProps = {
  closeModal: () => void;
  refetchOrder: () => Promise<unknown>;
};

const SUPPORTED_NEW_WORKFLOW = [ItemType.PartialDenture, ItemType.FullDenture];

export const CreateOrderAddProduct = ({
  closeModal,
  refetchOrder,
}: CreateOrderAddProductProps) => {
  const state = useLocation();
  const [teeth, setTeeth] = useState<number[]>([]);
  const [productGroupType, setProductGroupType] = useState<ItemType | null>(
    state.state?.productType ?? null
  );
  const navigate = useNavigate();
  const [
    newOrderId,
    productIndex,
    updateCreateOrderStep,
    updateCreateProductIndex,
    updateNewOrderId,
    updateProductNewWFStep,
  ] = useCreateOrderStore(state => [
    state.newOrderId,
    state.createProductIndex,
    state.updateCreateOrderStep,
    state.updateCreateProductIndex,
    state.updateNewOrderId,
    state.updateProductNewWFStep,
  ]);

  const {
    combinedWorkflowProducts,
    updateNewCombinedOrderId,
    updateCombinedWorkflowProducts,
  } = useCombinedWorkflowOrderStore();

  useEffect(() => {
    if ((state.state?.id && !newOrderId) || newOrderId !== state.state.id) {
      updateNewOrderId(state.state.id);
      updateNewCombinedOrderId(state.state.id);
    }
  }, [state.state?.id, newOrderId]);

  const [addProductToOrder] = useMutation(AddProductToOrderFromFormDocument, {
    refetchQueries: [GetOrderByUniqueAttributeDocument],
    awaitRefetchQueries: true,
  });
  const [getCreatingOrder] = useLazyQuery(GetOrderByUniqueAttributeDocument, {
    variables: {
      where: {
        id: newOrderId,
      },
    },
    fetchPolicy: "network-only",
  });
  const { pathname } = useLocation();

  const { isV2Product } = useV2ProductTypes();

  const { addOrderProduct } = useOrderConfiguration({
    orderId: newOrderId ?? "",
  });

  const handleSubmit = async () => {
    if (productGroupType && teeth.length > 0 && newOrderId) {
      // V1 -> V2
      if (isV2Product(productGroupType)) {
        const result = await addOrderProduct({
          type: productGroupType as unknown as ProductTypeV2,
          teeth,
          items: teeth.map(tooth => ({ tooth, type: productGroupType })),
        } as Parameters<typeof addOrderProduct>[0]);

        const products = result.data?.addOrderProduct?.products ?? [];
        const newProduct = products[products.length - 1];

        if (newProduct) {
          try {
            await refetchOrder();
            updateCreateProductIndex(products.length - 1);
            updateCreateOrderStep(CreateOrderStep.Product);
          } catch (e) {
            console.error(e);
            notifications.show({
              title: "Erreur",
              message:
                "Le produit a été ajouté mais la commande n'a pas pu être rechargée. Veuillez rafraîchir la page.",
              color: "red",
            });
          } finally {
            closeModal();
          }
        }
        return;
      }

      // V1 unchanged
      const teethString = teeth.join(",");
      const productInfo = productGroupType + " " + teethString;
      await addProductToOrder({
        variables: {
          productInfo: productInfo,
          orderId: newOrderId,
        },
      })
        .then(async () => await getCreatingOrder())
        .then(() => {
          updateCreateProductIndex(productIndex + 1);

          if (SUPPORTED_NEW_WORKFLOW.includes(productGroupType)) {
            updateCombinedWorkflowProducts([
              ...(combinedWorkflowProducts ?? []),
              productGroupType,
            ]);
          }
          if (pathname.includes("/create-new-order")) {
            updateProductNewWFStep(0);
            if (!SUPPORTED_NEW_WORKFLOW.includes(productGroupType)) {
              updateCreateOrderStep(CreateOrderStep.Product);
              navigate("/create-order", {
                state: {
                  id: newOrderId,
                },
              });
            } else {
              closeModal();
            }
          } else if (pathname.includes("/create-order")) {
            updateCreateOrderStep(CreateOrderStep.Product);
            if (SUPPORTED_NEW_WORKFLOW.includes(productGroupType)) {
              updateProductNewWFStep(0);
              navigate("/create-new-order", {
                state: {
                  id: newOrderId,
                  workflow: [
                    ...(combinedWorkflowProducts ?? []),
                    productGroupType,
                  ],
                  cameFromOtherProduct: true,
                },
              });
            }
          }
        });
    }
  };

  return (
    <CreateOrderAddProductForm
      teeth={teeth}
      setTeeth={setTeeth}
      productGroupType={productGroupType}
      setProductGroupType={setProductGroupType}
      handleSubmit={handleSubmit}
    />
  );
};
